import React from 'react';
import { Slider } from 'antd';
import { VideoEditPlaybackTimeDisplay } from './VideoEditPlaybackTimeDisplay';
import PlayheadController from '../../../components/common/Timeline/PlayheadController';
import { TranslatedTimelineSegments } from './TranslatedTimelineSegments';

export const VideoTimeBox: React.FC<any> = ({
    isPlaying,
    togglePlay,
    formatTime,
    duration,
    zoomLevel,
    handleZoomOut,
    handleZoomIn,
    handleZoomChange,
    handleZoomAfterChange,
    isFromPure,
    backgroundAudioEnabled,
    toggleBackgroundAudio,
    setBackgroundVolumePopupPosition,
    backgroundVolume,
    videoData,
    setBackgroundVolume,
    setShowBackgroundVolumePopup,
    sourceVideoRef,
    playheadTotalTimeRef,
    timelineScrubbingRef,
    translatedTimelineModels,
    timelineActiveTrack,
    timelineSelectedSubtitleId,
    timelineEditorRef,
    getTimelineTotalTimeSec
}) => {
    return (
        <>
            {/* 播放控制器 */}
            <div className="bofang">
                {/* 左侧播放按钮 */}
                <div className="left-controls">
                    <button
                        className="play-pause-btn"
                        onClick={togglePlay}
                    >
                        {isPlaying ? '⏸' : '▶'}
                    </button>
                    
                    {/* 当前时间和总时长 */}
                    <div className="time-display">
                        <div className="current-time">
                            <VideoEditPlaybackTimeDisplay formatTime={formatTime} />&nbsp;/
                        </div>
                        <div className="total-time">
                            {formatTime(duration)}
                        </div>
                    </div>
                </div>
                
                {/* 右侧控制区域 */}
                <div className="right-controls">
                    {/* 缩放控制 */}
                    <div className="zoom-controls">
                        <button className="zoom-btn" onClick={handleZoomOut}>−</button>
                        <div className="zoom-slider-container">
                            <Slider
                                className="zoom-slider-control"
                                min={0}
                                max={100}
                                value={zoomLevel}
                                onChange={handleZoomChange}
                                onAfterChange={handleZoomAfterChange}
                                tooltip={{ open: false }}
                                step={1}
                            />
                        </div>
                        <button className="zoom-btn" onClick={handleZoomIn}>+</button>
                    </div>
                    
                    {/* 背景音控制（纯净版隐藏） */}
                    {!isFromPure && (
                        <>
                            <div className="audio-controls">
                                <button 
                                    className={`audio-btn ${backgroundAudioEnabled ? 'active' : ''}`} 
                                    id="audioBtn" 
                                    onClick={toggleBackgroundAudio}
                                >
                                    {backgroundAudioEnabled ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                                        </svg>
                                    )}
                                </button>
                                <span className="audio-label">背景音</span>
                            </div>
                            
                            {/* 背景音音量调节 */}
                            <div
                                className="background-audio-volume-controls"
                                onClick={(e) => {
                                    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                                    // 在按钮上方显示弹窗，略微偏上
                                    setBackgroundVolumePopupPosition({
                                        top: rect.top - 140,
                                        left: rect.left,
                                    });
                                    // 使用当前任务级背景音量初始化（由 /video/task/get 的 volumeGainDb 决定）
                                    const initialVolume = backgroundVolume || (videoData as any)?.volumeGainDb || 0;
                                    setBackgroundVolume(initialVolume);
                                    setShowBackgroundVolumePopup(true);
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                </svg>
                                <span className="volume-label">背景音音量调节</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className={`timeBox${isFromPure ? ' timeBox--pure' : ''}`}>
                {/* 时间轴主体 */}
                <div className="timeline-container">
                    {/* 左侧固定区域 - 轨道标签 */}
                    <div className="timeline-left-panel">
                        <div className="track-labels-container">
                        {!isFromPure && (
                            <div className="track-label translated-track-label">
                                <span>翻译后</span>
                                <span className="track-audio-icon" aria-label="audio-indicator">
                                    {/* 音量图标（活动轨道显示） */}
                                    <svg className="icon-volume" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.06c1.48-.74 2.5-2.26 2.5-4.03z"/>
                                    </svg>
                                    {/* 静音图标（非活动轨道显示） */}
                                    <svg className="icon-muted" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zM19 12c0 .94-.2 1.82-.54 2.62l1.5 1.5C20.62 14.96 21 13.52 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77zM2.1 3.51L1 4.61l6.36 6.36H3v4h4l5 5v-6.73l5.39 5.39 1.11-1.1L2.1 3.51z"/>
                                    </svg>
                                </span>
                            </div>
                        )}
                            <div className="track-label original-track-label">
                                <span>原视频</span>
                                <span className="track-audio-icon" aria-label="audio-indicator">
                                    {/* 音量图标（活动轨道显示） */}
                                    <svg className="icon-volume" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.06c1.48-.74 2.5-2.26 2.5-4.03z"/>
                                    </svg>
                                    {/* 静音图标（非活动轨道显示） */}
                                    <svg className="icon-muted" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zM19 12c0 .94-.2 1.82-.54 2.62l1.5 1.5C20.62 14.96 21 13.52 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM2.1 3.51L1 4.61l6.36 6.36H3v4h4l5 5v-6.73l5.39 5.39 1.11-1.1L2.1 3.51z"/>
                                    </svg>
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    {/* 右侧滚动区域 - 时间轴刻度 */}
                    <div className="timeline-right-panel">
                        <div className="timeline-content">
                            <div className="time-ruler">
                                <div className="time-marks" id="timeMarks"></div>
                            </div>
                            
                            <div className="tracks-container">
                                {/* 播放进度线 */}
                                <div className="playhead" id="playhead">
                                    <div className="playhead-line"></div>
                                </div>
                                {/* 播放头由 rvfc/rAF 直接写 DOM transform，总时长从 ref 每帧读取，避免 props 变化重绑 */}
                                <PlayheadController
                                    videoRef={sourceVideoRef as React.RefObject<HTMLVideoElement | null>}
                                    totalTime={duration || 0}
                                    totalTimeRef={playheadTotalTimeRef}
                                    timelineContentSelector=".timeline-content"
                                    scrubbingRef={timelineScrubbingRef}
                                />
                                {!isFromPure && (
                                    <div className="track" id="translatedTrack">
                                        <div className="track-content">
                                            <TranslatedTimelineSegments
                                                segments={translatedTimelineModels}
                                                timelineActiveTrack={timelineActiveTrack}
                                                selectedSubtitleId={timelineSelectedSubtitleId}
                                                timelineEditorRef={timelineEditorRef}
                                                getTotalTimeSec={getTimelineTotalTimeSec}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="track" id="originalTrack">
                                    <div className="track-content">
                                        {/* 原视频轨道片段将通过JavaScript动态生成 */}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* 底部滚动条 */}
                        <div className="timeline-scrollbar">
                            <div className="scrollbar-track">
                                <div className="scrollbar-thumb" id="scrollbarThumb"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
